import {
  AlertCircle,
  ArrowRight,
  Bot,
  Boxes,
  CalendarPlus,
  FileText,
  GanttChart,
  GitCommit,
  History,
  ListChecks,
  MailQuestion,
  MessageSquare,
  MessagesSquare,
  ScrollText,
  Server,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useMemo } from "react";
import type { ComponentType } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { useAdminUtenti } from "@/hooks/useAdminUtenti";
import { useBacheca } from "@/hooks/useBacheca";
import { useMe } from "@/hooks/useMe";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";

interface AdminSection {
  to: string;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  status: "ready" | "wip";
}

const SECTIONS: AdminSection[] = [
  {
    to: "/admin/bacheca",
    label: "Bacheca",
    description: "Crea, modifica, chiudi voci. Gestisci i thread di messaggi col cliente.",
    icon: MessageSquare,
    status: "ready",
  },
  {
    to: "/admin/m1/upload",
    label: "M1 — Upload dati operativi",
    description: "Carica ordini, giacenze, piani e anagrafiche del modulo Cruscotto Ordini.",
    icon: Boxes,
    status: "ready",
  },
  {
    to: "/admin/utenti",
    label: "Utenti e accessi",
    description: "Allowlist email + invito magic link. Gestione ruoli e revoca.",
    icon: ShieldCheck,
    status: "ready",
  },
  {
    to: "/admin/ruoli",
    label: "Ruoli e permessi",
    description: "Configura il preset di permessi per ciascun ruolo (widget Dashboard, sezioni, moduli, bacheca). Override per singolo utente da 'Utenti e accessi'.",
    icon: ShieldCheck,
    status: "ready",
  },
  {
    to: "/admin/moduli",
    label: "Stato moduli",
    description: "Promuovi moduli lungo il ciclo di vita: roadmap → anteprima → beta → live. Visibilità cliente lato Mister Pet.",
    icon: GitCommit,
    status: "ready",
  },
  {
    to: "/admin/documenti",
    label: "Documenti",
    description: "Carica, organizza e governa documenti di progetto. Visibilità: solo Maitime o cliente + Maitime.",
    icon: FileText,
    status: "ready",
  },
  {
    to: "/admin/feed",
    label: "Review feed commit",
    description: "Bozze dai webhook GitHub. Riscrivi titolo e corpo in italiano, scegli modulo e pubblica per il cliente.",
    icon: GitCommit,
    status: "ready",
  },
  {
    to: "/admin/sistema",
    label: "Stato sistema",
    description: "Snapshot Cloud Run + Cloud SQL + cert SSL + versione DB. Per chi non ha accesso GCP Console.",
    icon: Server,
    status: "ready",
  },
  {
    to: "/admin/logs",
    label: "Log applicativi",
    description: "Log dei service Cloud Run con filtri (servizio, livello, periodo, ricerca testo).",
    icon: ScrollText,
    status: "ready",
  },
  {
    to: "/admin/ai-ops",
    label: "AI Ops",
    description: "Tracking chiamate Anthropic per modulo: cost monitoring, latenza, esiti. Foundation pronta — popolata appena parte M2.",
    icon: Bot,
    status: "ready",
  },
  {
    to: "/admin/agente-chat",
    label: "Chat con Aida",
    description: "Oversight delle conversazioni in chat con l'agente documenti: trascrizione, strumenti consultati e costo AI. Sola lettura.",
    icon: Bot,
    status: "ready",
  },
  {
    to: "/admin/audit",
    label: "Audit log",
    description: "Tracciabilità delle mutazioni applicative (bacheca, moduli, utenti, documenti, feed). Filtri evento/entità/periodo.",
    icon: History,
    status: "ready",
  },
  {
    to: "/admin/conversazioni",
    label: "Conversazioni",
    description: "Registra chiamate, email, videocall, incontri. Le visibili cliente compaiono nel suo registro Conversazioni.",
    icon: MessagesSquare,
    status: "ready",
  },
  // Le due leve della Panoramica del cliente. Stanno qui e non fra le voci di
  // sistema perché è roba che Enzo vede: la casella accanto al Gantt e il KPI
  // in testa alla pagina.
  {
    to: "/admin/gantt",
    label: "Timeline di progetto",
    description: "La roadmap che il cliente vede: barre per modulo, fasi libere (collaudo, formazione), milestone e finestre esterne. Quello che scrivi qui non lo riscrive più nessun deploy.",
    icon: GanttChart,
    status: "ready",
  },
  {
    to: "/admin/settimana",
    label: "Questa settimana",
    description: "Cosa stiamo facendo adesso, in parole da cliente. Compare nella Panoramica accanto alla timeline — e si spegne da sola se resta ferma più di due settimane.",
    icon: ListChecks,
    status: "ready",
  },
  {
    to: "/admin/incontri",
    label: "Incontri",
    description: "Call e incontri in programma. Il primo appuntamento futuro visibile al cliente è quello che compare nel KPI «Prossimo incontro».",
    icon: CalendarPlus,
    status: "ready",
  },
];

export function AdminPage() {
  const { data: me, isLoading } = useMe();
  const { data: voci = [], isLoading: loadingVoci } = useBacheca({});
  const { data: utenti = [], isLoading: loadingUtenti } = useAdminUtenti();

  const widgets = useMemo(() => {
    const aperteVersoCliente = voci.filter(
      (v) => v.stato !== "chiuso" && v.parte_responsabile === "misterpet"
    );
    const aperteVersoMaitime = voci.filter(
      (v) =>
        v.stato !== "chiuso" &&
        (v.parte_responsabile === "maitime" || v.parte_responsabile === "entrambi")
    );
    const nonLetti = aperteVersoCliente.filter((v) => v.ha_messaggi_non_letti).length;
    const utentiAttivi = utenti.filter((u) => u.attivo).length;
    const utentiMaiEntrati = utenti.filter(
      (u) => u.attivo && !u.primo_accesso_il
    ).length;
    return {
      verso_cliente: aperteVersoCliente.length,
      verso_maitime: aperteVersoMaitime.length,
      non_letti: nonLetti,
      utenti_attivi: utentiAttivi,
      utenti_mai_entrati: utentiMaiEntrati,
    };
  }, [voci, utenti]);

  if (!isLoading && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContainer>
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Admin</h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Pannello operativo interno Maitime: gestione bacheca, dati operativi M1, e (in
          arrivo) utenti, stato moduli, documenti, review feed.
        </p>
      </header>

      {/* Widget operativi */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Widget
          to="/admin/bacheca?tab=verso_cliente"
          icon={MailQuestion}
          label="Bacheca · verso cliente"
          value={loadingVoci ? "—" : String(widgets.verso_cliente)}
          hint="voci aperte"
          dot={widgets.non_letti > 0 ? "destructive" : undefined}
          dotLabel={widgets.non_letti > 0 ? `${widgets.non_letti} non letti` : undefined}
        />
        <Widget
          to="/admin/bacheca?tab=verso_maitime"
          icon={AlertCircle}
          label="Bacheca · verso Maitime"
          value={loadingVoci ? "—" : String(widgets.verso_maitime)}
          hint="su cui dobbiamo agire"
        />
        <Widget
          to="/admin/utenti"
          icon={Users}
          label="Utenti attivi"
          value={loadingUtenti ? "—" : String(widgets.utenti_attivi)}
          hint={
            widgets.utenti_mai_entrati > 0
              ? `${widgets.utenti_mai_entrati} non sono mai entrati`
              : "tutti hanno fatto almeno un accesso"
          }
        />
        <Widget
          icon={GitCommit}
          label="Bozze feed"
          value="—"
          hint="in costruzione"
          disabled
        />
      </section>

      {/* Card sub-feature */}
      <section className="space-y-2">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Sezioni
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const disabled = s.status === "wip";
            const card = (
              <div
                className={cn(
                  "rounded-lg border bg-card/40 p-4 space-y-2 transition-colors h-full",
                  disabled ? "opacity-60" : "hover:bg-accent/40"
                )}
              >
                <div className="flex items-center justify-between">
                  <Icon className="size-4 text-brand-700" />
                  {!disabled && <ArrowRight className="size-4 text-muted-foreground" />}
                </div>
                <h3 className="text-sm font-medium text-foreground">{s.label}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {s.description}
                </p>
                {disabled && (
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 pt-1">
                    in costruzione
                  </p>
                )}
              </div>
            );
            return disabled ? (
              <div key={s.label}>{card}</div>
            ) : (
              <Link key={s.label} to={s.to}>
                {card}
              </Link>
            );
          })}
        </div>
      </section>
    </PageContainer>
  );
}

interface WidgetProps {
  to?: string;
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: string;
  disabled?: boolean;
  dot?: "destructive";
  dotLabel?: string;
}

function Widget({ to, icon: Icon, label, value, hint, disabled, dot, dotLabel }: WidgetProps) {
  const card = (
    <div
      className={cn(
        "rounded-lg border bg-card p-4 space-y-1 transition-colors h-full",
        disabled ? "opacity-60" : "hover:bg-accent/40"
      )}
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="size-3.5" />
        <span className="uppercase tracking-wider">{label}</span>
        {dot && <span className={cn("size-1.5 rounded-full bg-destructive ml-auto")} title={dotLabel} />}
      </div>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
  if (disabled || !to) return card;
  return <Link to={to}>{card}</Link>;
}
